import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
import moment from "moment";
import ReactECharts from "echarts-for-react";
import { LineChart, LineSeriesOption } from "echarts/charts";
import {
    GridComponent,
    GridComponentOption,
    TooltipComponent,
    TooltipComponentOption,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";

type EChartsOption = echarts.ComposeOption<
    LineSeriesOption | GridComponentOption | TooltipComponentOption
>;

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);
interface SparklineChartProps {
    /** Pre-built line series segments from `buildSparklineSeriesWithMissingGaps`. */
    gapSplitSeries: LineSeriesOption[];
    color?: string;
    height?: number;
    width?: number;
}

export const SparklineChart = ({
    gapSplitSeries,
    color = "#4a73e6",
    height = 40,
    width = 100,
}: SparklineChartProps) => {
    const { longDateTimeFormat } = useDateTimeFormatStore((state) => ({
        longDateTimeFormat: state.longDateTimeFormat,
    }));

    const option: EChartsOption = {
        animation: true,
        grid: {
            left: 6,
            right: 6,
            top: 6,
            bottom: 6,
        },
        xAxis: {
            type: "time",
            boundaryGap: ["0%", "0%"],
            show: false,
        },
        yAxis: {
            type: "value",
            show: false,
        },
        tooltip: {
            trigger: "axis",
            renderMode: "html",
            /**
             * By default, ECharts tooltips are rendered within the chart container. 
             * Setting `appendToBody` to `true` allows the tooltip to be rendered as a child of the body element, 
             * which can help prevent clipping issues because this widget has limited size and the tooltip may be cut off.
             */
            appendTo: "body",
            axisPointer: {
                type: "line",
                lineStyle: {
                    color: "#999",
                    width: 1,
                    type: "dashed",
                },
                label: {
                    formatter: (params: any) => {
                        return `${moment(params.value, "x").format(
                            longDateTimeFormat
                        )}`;
                    },
                },
            },
            backgroundColor: "#fff",
            textStyle: {
                color: "#333",
            },
            show: true,
        },
        series: gapSplitSeries,
    };

    return (
        <div style={{ height: `${height}px`, width: `${width}%` }}>
            <ReactECharts
                className="chart-box"
                option={option}
                notMerge={true}
                lazyUpdate={true}
            />
        </div>
    );
};
